import { ChangeEvent, KeyboardEvent, useEffect } from 'react';
import { useLocation } from 'react-router';
import { Box, Card, CardActions, Typography } from '@mui/material';
import FormTextField from '../../common/components/FormTextField';
import PasswordTextField from '../../resetPassword/components/PasswordTextField';
import PrimaryButton from '../../common/components/PrimaryButton';
import ServerErrorAlert from '../../common/components/ServerErrorAlert';
import TertiaryButton from '../../common/components/TertiaryButton';
import LoadingDialog from './LoadingDialog';
import useScreenDimensions from '../../common/hooks/useScreenDimensions';
import useFormManager from '../../common/hooks/useFormManager';
import useValidatedField from '../../common/hooks/useValidatedField';
import useCoachConversion from './hooks/useCoachConversion';
import { navigate } from '../../state/navigation/navigation';

type ConvertFormLocationState = {
    email?: unknown;
};

export default function CoachTrialConvertForm() {
    const { width } = useScreenDimensions();
    const location = useLocation<ConvertFormLocationState>();
    const prefilledEmail = typeof location.state?.email === 'string' ? location.state.email : '';

    const { showValidationErrors, setShowValidationErrors, notNullValidator, emailValidator } = useFormManager();
    const {
        value: email,
        setValue: setEmail,
        validated: emailValidated,
        errorState: emailErrorMessage
    } = useValidatedField(emailValidator);
    const {
        value: password,
        setValue: setPassword,
        validated: passwordValidated,
        errorState: passwordErrorMessage
    } = useValidatedField(notNullValidator);

    const {
        convertExistingAccount,
        isInProcess,
        isOpen,
        progress,
        serverError,
        setServerError,
        loginErrorMessage,
        setLoginErrorMessage
    } = useCoachConversion();

    useEffect(() => {
        if (prefilledEmail) {
            setEmail(prefilledEmail);
        }
    }, [prefilledEmail, setEmail]);

    useEffect(() => {
        setLoginErrorMessage('');
    }, [email, password, setLoginErrorMessage]);

    const hasEmailError = Boolean(loginErrorMessage) || (showValidationErrors && !emailValidated);
    const hasPasswordError = Boolean(loginErrorMessage) || (showValidationErrors && !passwordValidated);

    const handleConvertAccount = async () => {
        const formFieldsValid = emailValidated && passwordValidated;
        setShowValidationErrors(true);

        if (!formFieldsValid || isInProcess) {
            return;
        }

        await convertExistingAccount(email, password, {
            accountCreationLocation: 'account'
        });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        const enterKeyPressed = event.key === 'Enter' || event.keyCode === 13 || event.which === 13;

        if (enterKeyPressed) {
            handleConvertAccount();
        }
    };

    return (
        <Box className="container payment-container coach-trial-convert-form">
            <Box>
                <Card data-testid="card" classes={{ root: 'form-card card' }}>
                    <Box className="title-container">
                        <Typography className="title" component="div">
                            Log in to start your
                            <Box component="span" display="block">
                                14-day coach trial
                            </Box>
                        </Typography>
                        {width > 375 ? (
                            <Box
                                component="img"
                                alt="highlight"
                                className="title-highlight"
                                height={50}
                                width={350}
                                src="https://static.trainheroic.com/icons/Highlight.Long2.png"
                            />
                        ) : (
                            <Box className="title-placeholder" />
                        )}
                    </Box>
                    <Typography className="form-padding padding-desktop conversion-copy" sx={{ mb: 2 }}>
                        Use your existing TrainHeroic account. Your athlete data and login stay the same.
                    </Typography>
                    <Box component="form" className="form-padding padding-desktop coach-trial-form">
                        <Box className="form-input">
                            <FormTextField
                                onKeyDown={handleKeyDown}
                                required
                                type="email"
                                label="Email"
                                value={email}
                                variant="standard"
                                onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
                                error={hasEmailError}
                                helperText={hasEmailError ? loginErrorMessage || emailErrorMessage : ''}
                            />
                        </Box>
                        <Box className="form-input">
                            <PasswordTextField
                                onKeyDown={handleKeyDown}
                                required
                                label="Password"
                                value={password}
                                onChange={value => setPassword(value)}
                                hasError={hasPasswordError}
                                helperText={hasPasswordError ? loginErrorMessage || passwordErrorMessage : ''}
                            />
                        </Box>
                    </Box>
                    <CardActions classes={{ root: 'action-container' }}>
                        <Box className="action-buttons-container">
                            <PrimaryButton
                                showActivity={isInProcess}
                                disabled={isInProcess}
                                onClick={handleConvertAccount}
                            >
                                Convert My Account to a Coach Trial
                            </PrimaryButton>
                            <Typography className="cta-reassurance">
                                No credit card required &bull; Cancel anytime
                            </Typography>
                        </Box>
                        <Box className="action-buttons-container">
                            <TertiaryButton onClick={() => navigate('/forgot-password')}>Forgot Password</TertiaryButton>
                        </Box>
                    </CardActions>
                </Card>
                <Box className="action-buttons-container button-margin">
                    <TertiaryButton className="coach-trial-convert-link" onClick={() => navigate('/create-account')}>
                        Create a New Account Instead
                    </TertiaryButton>
                </Box>
                <LoadingDialog isOpen={isOpen} progress={progress} />
            </Box>
            <ServerErrorAlert
                title="There was an error converting your account."
                open={serverError}
                handleClose={() => setServerError(false)}
            />
        </Box>
    );
}
